import { useParams } from 'react-router-dom';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';

/**
 * Interface for route parameters
 */
export interface RouteParams {
  projectId?: string;
  cohortId?: string;
  cohortVersionId?: string;
}

/**
 * Helper function to extract route parameters from URL pathname
 * Used as fallback when router context is not available (e.g., global components)
 */
const getRouteParamsFromUrl = (): RouteParams => {
  try {
    const pathname = window?.location?.pathname || '';

    // Extract projectId from patterns like /projects/{projectId}/...
    const projectIdMatch = pathname.match(/\/projects\/([^/]+)/);
    const projectId = projectIdMatch?.[1];

    // Extract cohortId from patterns like /cohort-builder/{cohortId} or /cohorts/{cohortId}
    const cohortIdMatch = pathname.match(/\/(cohort-builder|cohorts)\/([^/]+)/);
    const cohortId = cohortIdMatch?.[2];

    // Extract cohortVersionId - typically after cohort-version segment
    // Pattern: /cohort-builder/{cohortId}/cohort-version/{cohortVersionId}
    const cohortVersionIdMatch = pathname.match(
      /\/(cohort-builder|cohorts)\/[^/]+\/cohort-version\/([^/]+)/
    );
    const cohortVersionId = cohortVersionIdMatch?.[2];

    return {
      projectId,
      cohortId,
      cohortVersionId,
    };
  } catch (error) {
    console.warn('Failed to extract route params from URL:', error);
    return {
      projectId: undefined,
      cohortId: undefined,
      cohortVersionId: undefined,
    };
  }
};

/**
 * Hook to safely get route parameters with fallback support
 *
 * Strategy:
 * 1. First tries to get params from useParams() (standard router context)
 * 2. If feature flag is enabled and router params are missing, falls back to URL extraction
 *
 * This ensures compatibility with both:
 * - Standard components within router context
 * - Global components outside router context (when feature flag is enabled)
 */
export const useSafeRouteParams = (): RouteParams => {
  // Get route params from router context (standard approach)
  const routerParams = useParams();

  // Get route params from URL as fallback (for global components)
  const urlParams = getRouteParamsFromUrl();

  // For each param, use router param first, then fall back to URL param if undefined
  return {
    projectId: routerParams?.projectId ?? urlParams.projectId,
    cohortId: routerParams?.cohortId ?? urlParams.cohortId,
    cohortVersionId: routerParams?.cohortVersionId ?? urlParams.cohortVersionId,
  };
};
